import { Component, computed, effect, input, model, output, ChangeDetectionStrategy } from '@angular/core';
import { ErrorRuleConditionType } from '../../models/error-rule-condition-type';
import { ErrorRuleCondition } from '../../models/error-rule-condition';
import { ControlContainer, NgModelGroup, FormsModule } from '@angular/forms';
import { ErrorRuleConditionValue } from '../../models/error-rule-condition-value';
import { computedAsync } from 'src/app/shared/signal-utilities';
import { ErrorRuleConditionComparer } from '../../models/error-rule-condition-comparer';
import { ErrorRuleConditionTypeGroup } from '../../models/error-rule-condition-type-group';
import { LayoutRowComponent } from '../../../controls/components/basic/layout-row/layout-row.component';
import { LayoutColumnComponent } from '../../../controls/components/basic/layout-column/layout-column.component';
import { SingleselectboxComponent } from '../../../controls/components/basic/singleselectbox/singleselectbox.component';
import { PartnerSelectorComponent } from '../../../partner/partner-selector.component';
import { TextboxComponent } from '../../../controls/components/basic/textbox/textbox.component';
import { LinkComponent } from '../../../controls/components/basic/link/link.component';
import { SlicePipe } from '@angular/common';

@Component({
    selector: 'app-error-rules-condition',
    templateUrl: './error-rules-condition.component.html',
    styleUrls: ['./error-rules-condition.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [LayoutRowComponent, LayoutColumnComponent, SingleselectboxComponent, FormsModule, PartnerSelectorComponent, TextboxComponent, LinkComponent, SlicePipe]
})
export class ErrorRulesConditionComponent {
  condition = model.required<ErrorRuleCondition>();
  conditionTypeProvider = input.required<ConditionTypeProviderFn>()
  conditionValueProvider = input.required<ConditionValueProviderFn>()
  onDeleteCondition = output();
  onSummaryChange = output<string>();

  constructor() {
    effect(() => {
      const condtionType = this.conditionTypes()?.find(c => c.key == this.condition()?.TypeKey)?.name ?? ""
      const subType = this.subConditionTypes()?.find(c => c.key == this.condition()?.SubTypeKey)?.name ?? ""
      const comparer = this.comparers()?.find(c => c.key == this.condition().ComparerKey)?.name ?? ""
      
      const valuesStr = this.condition().Values.map(val => {
        if (this.values()?.length > 0) {
          var value = this.values()?.find(v => v.key == val.value)
          return value ? "\"" + value.name + "\"" : "";
        }
        else if (this.isPartnerLookup()) {
          return val.name ? "\"" + val.name + "\"" : "";
        }
        else {
          return val.value ? "\"" + val.value + "\"" : "";
        }
      }).join(" or ");
      
      const subTypeStr = subType ? " (" + subType + ")" : ""
      const comparerStr = comparer ? " " + comparer + " " : ""
      const summary = condtionType + subTypeStr + comparerStr + valuesStr;
      this.onSummaryChange.emit(summary);
    });
  }


  //** Dropdown sources */
  conditionTypes = computedAsync(async (): Promise<ErrorRuleConditionType[]> => {
    return await this.conditionTypeProvider()(null);
  });

  groupedConditionTypes = computed((): ErrorRuleConditionTypeGroup[] => {
    const types = this.conditionTypes() ?? [];
    var groups = types.map((t) => t.group);
    groups = [...new Set(groups)]
    const groupedTypes = groups.map((g) => {
      const items = types.filter(t => t.group === g);
      return {key: g, items: items};
    })
    return groupedTypes;
  });

  subConditionTypes = computedAsync(async (): Promise<ErrorRuleConditionType[]> => {
    if (this.condition().TypeKey) {
      return await this.conditionTypeProvider()(this.condition().TypeKey);
    }
    else {
      return []
    }
  });

  comparers = computed((): ErrorRuleConditionComparer[] => {
    const selectedConditionType = this.conditionTypes()?.find(c => c.key == this.condition().TypeKey)
    
    var selectedSubConditionType: ErrorRuleConditionType = null
    if (this.condition().SubTypeKey) {
      selectedSubConditionType = this.subConditionTypes()?.find(c => c.key == this.condition().SubTypeKey)
    }

    const comparers = selectedSubConditionType ? selectedSubConditionType?.comparers : selectedConditionType?.comparers
    const newComparers = comparers?.map(c => {
      const newComparer = {...c};
      newComparer["disabled"] = newComparer.key=="NotEquals" && this.condition().Values.length > 1;
      return newComparer;
    }) ?? [];
    return newComparers;
  });

  values = computedAsync(async (): Promise<ErrorRuleConditionValue[]> => {
    return (await this.conditionValueProvider()(this.condition().TypeKey, this.condition().SubTypeKey)) ?? []
  });


  //** Computed booleans for enable/disable fields */
  subTypeEnabled = computed(() => {
    return this.condition().TypeKey != null;
  });

  comparerEnabled = computed(() => {
    if (this.condition().TypeKey != null) {
      if (this.hasSubTypes()) {
        if (this.condition().SubTypeKey != null) {
          return true;
        }
        else {
          return false;
        }
      }
      else {
        return true;
      }
    }
    else {
      return false;
    }
  });

  valueEnabled = computed(() => {
    return this.condition().ComparerKey != null && this.condition().TypeKey != null;
  });

  orEnabled = computed(() => {
    return this.valueEnabled() && this.condition().ComparerKey != "NotEquals";
  });


  //** Change event handlers */
  onConditionTypeChange(key: string) {
    if (this.condition().TypeKey !== key) {
      this.condition.update(c => ({...c, TypeKey: key, SubTypeKey: null, ComparerKey: null, Values: [this.getNewValue()]}));
    }    
  }

  onSubConditionTypeChange(key: string) {
    if (this.condition().SubTypeKey !== key) {
      this.condition.update(c => ({...c, SubTypeKey: key, ComparerKey: null, Values: [this.getNewValue()]}));
    }
  }

  onComparerChange(key: string) {
    if (this.condition().ComparerKey !== key) {
      this.condition.update(c => ({...c, ComparerKey: key}));
    }
  }

  onValueChange(idx: number, value: any) {
    if (this.isPartnerLookup()) {
      if (this.condition().Values[idx].value !== value?.PartnersId) {
        this.condition.update(c => {
          const newValues = [...c.Values];
          newValues[idx].value = value?.PartnersId;
          newValues[idx].name = value?.PartnerName;
          return {...c, Values: newValues};
        });
      }
    }
    else
    {
      if (this.condition().Values[idx].value !== value) {
        this.condition.update(c => {
          const newValues = [...c.Values];
          newValues[idx].value = value;
          return {...c, Values: newValues};
        });
      }
    }
  }


  //** User initiated actions */
  addValue(e: any) {
    this.condition.update(c => {
      const newValues = [...c.Values, this.getNewValue()];
      return {...c, Values: newValues};
    });
  }

  deleteValue(idx: number) {
    this.condition.update(c => {
      const newValues = [...c.Values];
      newValues.splice(idx+1, 1)
      return {...c, Values: newValues};
    });
  }

  deleteCondition() {
    this.onDeleteCondition.emit();
  }


  //** Helpers*/
  isPartnerLookup = computed(() => {
    return this.condition().TypeKey == 'SenderPartner' || this.condition().TypeKey == 'ReceiverPartner' || this.condition().TypeKey == 'SenderReceiverPartner'
  });
  
  hasSubTypes = computed(() => {
    return this.subConditionTypes()?.length > 0
  });

  getNewValue() {
    const newTempId = 'NEW_' + Math.floor((Math.random() * 100000) + 1);
    return {id: newTempId, value: null, name: null}
  }
}


export type ConditionValueProviderFn = (typeKey: string, subTypeKey: string) => Promise<ErrorRuleConditionValue[]>;
export type ConditionTypeProviderFn = (typeKey: string) => Promise<ErrorRuleConditionType[]>;